import { Injectable, Renderer2 } from '@angular/core';
import { HeroTallContent } from '../models/hero-tall-content.model';
import { BreadcrumbInput, BreadcrumbModel, BreadcrumbTheme } from '@challenger6/vm-pattern-library/breadcrumb';
import { ButtonLevel, ButtonStyle, ButtonType } from '@challenger6/vm-pattern-library/button';
import { BtnVariant } from '@challenger6/vm-pattern-library/hero/hero-tall/hero-tall.interface';
import { AccordionContent } from '../models/accordion-content.model';
import { AccordionItemContent } from '../models/accordion-item-content.model';
import { GlobalElementType } from '../enums/global-element-type-uid.enum';
import { SettingsType } from '../enums/settings-type.enum';
import { LinksGroupContent } from '../models/links-group-content.model';
import { MediaTilesContent, MediaTilesItemContent } from '../models/media-tiles-content.model';
import { TabContent, TabItemContent } from '../models/tab-content.model';
import { TextContent, TextItemContent } from '../models/text-content.model';
import { TilesContent } from '../models/tiles-content.model';
import { TileWithFAIconSmallContent } from '../models/tile-with-fa-icon-small-content.model';
import { ArticleListContent, ArticleListContents } from '../models/article-list.model';
import { ActionGroupContent } from '../models/action-group-content.model';
import { FeatureArticleContent, FeatureArticleContents } from '../models/feature-article.model';
import { OrderListContent, OrderListContents } from '../models/ordered-list.model';
import { LinkInfo } from '@challenger6/vm-pattern-library/links-groups';
import { SelectableCardContent, SelectableCardContents } from '../models/selectable-card.model';
import { CampaignContentService } from '@challenger/care/services/campaign-content.service';
import { IconWeight } from '@challenger/care/models/icon-weight.enum';
import {
  IGlobalComponent,
  IGenericComponent,
  ISEOLandingPage,
  ICTA,
  IMedia,
} from '../../common/interfaces/seo-landing-page-collection.interface';
import { Setting } from '../models/setting.model';
import { PostcodeCheckerContent } from '../models/postcode-checker-content.model';
import { environment } from '../../../environments/environment';
import { PageSectionItemCTA } from '../models/page-section-item-cta.model';
import { PageSectionItemMedia } from '../models/page-section-item-media.model';
import { PageSection } from '../models/page-section.model';
import { Settings } from '../enums/settings.enum';
import { CountdownContent } from '../models/count-down-content.model';
import { Upgrade360Content } from '../models/upgrade-360-section.model';

@Injectable({
  providedIn: 'root',
})
export class SectionService {
  breadcrumbs: BreadcrumbModel[] = [
    { name: 'Help', routerLink: 'https://virginmedia.com/help' },
    { name: 'Broadband', routerLink: 'https://virginmedia.com/help/broadband' },
    { name: 'TV', routerLink: 'https://virginmedia.com/help/tv' },
    { name: 'Existing Customer Deals', routerLink: 'https://virginmedia.com/existing-customer-deals' },
    {
      name: 'Billing and payments',
      routerLink: 'https://www.virginmedia.com/help/billing-and-payment/my-virgin-media/billing',
    },
  ];

  constructor(private campaignContentService: CampaignContentService) {}

  public getComponentContent(globalElementTypeUID: string, data: any, sesData: any): any {
    let config: any = null;

    switch (globalElementTypeUID) {
      case GlobalElementType.AccordionLeftAligned: {
        config = this.getAccordionContent(data);
        break;
      }
      case GlobalElementType.ActionCenterAlignWhiteBg: {
        config = this.getActionContent(data);
        break;
      }
      case GlobalElementType.ArticleListing: {
        config = this.getArticleList(data);
        break;
      }
      case GlobalElementType.CountdownCenterAligned: {
        config = this.getCountdownContent(data);
        break;
      }
      case GlobalElementType.FeatureArticle: {
        config = this.getFeatureArticle(data);
        break;
      }
      case GlobalElementType.FloatingBanner: {
        config = new PageSection();
        break;
      }
      case GlobalElementType.LinksLeftAligned: {
        config = this.getLinksGroupContent(data);
        break;
      }
      case GlobalElementType.PostcodeChecker: {
        config = this.getPostcodeCheckerContent(data);
        break;
      }
      case GlobalElementType.ExploreCards: {
        config = this.getTabContent(data);
        break;
      }      
      case GlobalElementType.TextSectionLeftAlignedWhiteBg:
      case GlobalElementType.TextSectionLeftAligned:
      case GlobalElementType.TextSectionCenterAlignedWhiteBg:
      case GlobalElementType.TextSectionCenterAligned:
      case GlobalElementType.ModalSection: {
        config = this.getTextContent(data);
        break;
      }
      case GlobalElementType.TileCenterAlignedWhiteBg:
      case GlobalElementType.TileCenterAlignedWhiteBgClick:
      case GlobalElementType.TileLeftAlignedWhiteBg:
      case GlobalElementType.TileLeftAlignedWhiteBgClick:
      case GlobalElementType.TileRightAlignWhiteBg:
      case GlobalElementType.TileRightAlignWhiteBgClick: {
        config = this.getTilesContent(data, sesData);
        break;
      }
      case GlobalElementType.TileFullWidthClickable: {
        config = this.getSelectableCardList(data);
        break;
      }
      case GlobalElementType.MediaTiles: {
        config = this.getMediaTilesContent(data);
        break;
      }
      case GlobalElementType.OrderedList: {
        config = this.getOrderList(data);
        break;
      }
      case GlobalElementType.Upgrade360Form: {
        config = this.getUpgrade360Content(data);
        break;
      }
    }
    if (config) {
      config.id = data?.contentSectionID || data?.id;
      config.type = data?.globalElementType?.uid || data?.globalElementType?.title;
      config.title = data?.title;
      config.copy = data?.copy;
      config.settings = this.getSectionSettings(data);
    }
    return config;
  }

  public getHeroContent(renderer: Renderer2, data: any, overrideBackgroundColour: any = null): HeroTallContent | null {
    const header = document.getElementById('tlp-hdr-bg');
    const content = document.getElementsByClassName('app')[0];
    const footer = document.getElementsByClassName('footer')[0];
    const backgroundColour = overrideBackgroundColour || data?.hero_banner?.BackgroundColour;
    let buttonVariant = 'SECONDARY';
    if (header !== null) {
      if (backgroundColour=== 'red') {
        renderer.setStyle(header, 'background-color', '#ed0000');
        renderer.setStyle(footer, 'background-color', '#ed0000');
        buttonVariant = 'SECONDARY';
      } else if (backgroundColour=== 'black'){
        renderer.setStyle(header, 'background-color', '#000');
        renderer.addClass(content, 'black-friday');        
        renderer.setStyle(footer, 'background-color', '#000');        
        buttonVariant = 'PRIMARY';
      } else {
        renderer.setStyle(header, 'background-color', '#322332');
        renderer.setStyle(footer, 'background-color', '#322332');
        buttonVariant = 'TERTIARY';
      }
    }
    if (data?.hero_banner) {
      return {
        theme: data?.hero_banner?.FlareType,
        heroHeader: data?.headerTemplate?.heading1,
        buttonText: data?.headerTemplate?.ctas[0]?.label,
        btnType: ButtonType.LINK,
        btnVariant: buttonVariant, 
        background: backgroundColour,
        buttonLink: this.campaignContentService.appendCampaignCodeToQueryString(data?.headerTemplate?.ctas[0]?.url),
        breadcrumb: this.getBreadCrumbs(data?.articleTemplate?.breadcrumbs),
        heroText: data?.headerTemplate?.copy,
        heroImage: `${environment.strapiURL}${data?.headerTemplate?.headerMedia[0]?.large?.url}`,
        heroMobileImage: `${environment.strapiURL}${data?.headerTemplate?.headerMedia[0]?.small?.url}`,
      } as HeroTallContent;
    }
    if (header !== null) {
      renderer.setStyle(header, 'margin-bottom', '0rem');
    }
    return null;
  }

  public getBreadCrumbs(breadcrumbs): BreadcrumbInput {
    if (breadcrumbs && breadcrumbs.includes('<')) {
      const breadcrumbsExtra = breadcrumbs.split('<');
      // eslint-disable-next-line prefer-const
      let tempBreadcrumbs = new Array<BreadcrumbModel>();
      for (const breadcrumb of breadcrumbsExtra) {
        for (const localBreadcrumb of this.breadcrumbs) {
          if (breadcrumb === localBreadcrumb.name) {
            tempBreadcrumbs.push(localBreadcrumb);
          }
          if (tempBreadcrumbs.length === breadcrumbsExtra.length - 1) {
            tempBreadcrumbs.push({
              routerLink: './',
              name: breadcrumbsExtra[breadcrumbsExtra.length - 1],
            });
          }
        }
      }
      this.breadcrumbs = tempBreadcrumbs;
      return {
        breadcrumbs: this.breadcrumbs,
        breadcrumbNumber: breadcrumbs ? this.breadcrumbs.length : 2,
        theme: BreadcrumbTheme.DARK,
        homePage: 'https://virginmedia.com/',
      } as BreadcrumbInput;
    }
  }

  public getAccordionContent(data): AccordionContent {
    const items = new Array<AccordionItemContent>();
    data?.genericComponent.forEach((itemData) => {
      items.push({
        title: itemData?.title,
        copy: itemData?.copy,
        settings: this.getItemSettings(itemData),
      } as AccordionItemContent);
    });
    return {
      items,
    } as AccordionContent;
  }

  public getActionContent(data): ActionGroupContent {
    const actions = [
      {
        uid: '',
        url: '',
        label: '',
        type: ButtonType.BUTTON,
        buttonLevel: ButtonLevel.TERTIARY,
      },
      {
        uid: '',
        url: '',
        label: '',
        type: ButtonType.BUTTON,
        buttonLevel: ButtonLevel.TERTIARY,
        buttonStyle: ButtonStyle.OUTLINE,
      },
    ];

    data?.genericComponent[0]?.ctas?.forEach((cta, index) => {
      let buttonType;

      switch (cta?.contentUid) {
        case 'modal':
          buttonType = ButtonType.MODAL;
          break;
        case 'link':
          buttonType = ButtonType.LINK;
          break;
        case 'router':
          buttonType = ButtonType.ROUTER_LINK;
          break;
        case 'countdown':
          buttonType = ButtonType.COUNTDOWNCTA;
          break;
        default:
          buttonType = actions[index]?.type;
          break;
      }

      if (cta?.Uid === 'chat') {
        buttonType = ButtonType.CHAT;
      }
      if (cta?.dataAttr === 'outline') {
        actions[index].buttonStyle = ButtonStyle.OUTLINE;
      }

      actions[index] = {
        ...actions[index],
        type: buttonType,
        label: cta?.label,
        uid: cta?.Uid,
        url: cta?.url,
      };
    });

    actions.length = data?.genericComponent[0]?.ctas?.length;
    return {
      actions,
    } as ActionGroupContent;
  }

  public getArticleList(data): ArticleListContent {
    const items: ArticleListContents[] = [];
    data?.genericComponent.forEach((itemData) => {
      items.push({
        title: itemData.title,
        copy: itemData.copy,
        image: environment.strapiURL + itemData.icon[0].url,
        iconAlt: itemData.fa_icon_name ? itemData.fa_icon_name : itemData.title,
        settings: this.getItemSettings(itemData),
      });
    });
    return {
      items,
    };
  }

  public getCountdownContent(data): CountdownContent {
    return {
      endDate: new Date(data?.genericComponent[0]?.title),
    } as CountdownContent;
  }

  public getFeatureArticle(data): FeatureArticleContent {
    const items: FeatureArticleContents[] = [];

    data?.genericComponent.forEach((itemData) => {
      items.push({
        image: environment.strapiURL + itemData?.icon[0]?.url,
        imageAltText: itemData.title,
        title: itemData.title,
        uid: itemData.uid,
        ctas: this.getCTAs(itemData.ctas),
        settings: this.getItemSettings(itemData),
      });
    });
    return {
      items,
    };
  }

  public getLinksGroupContent(data): LinksGroupContent {
    const items = new Array<LinkInfo>();
    data?.genericComponent[0]?.listOfLinks[0].links.forEach((itemData) => {
      items.push({
        label: itemData?.label,
        url: itemData?.url,
        settings: this.getItemSettings(itemData),
      } as LinkInfo);
    });
    return {
      // type: GlobalElementType.LinksLeftAligned,
      // title: data?.title,
      items,
    } as LinksGroupContent;
  }

  public getMediaTilesContent(data): MediaTilesContent {
    const items = new Array<MediaTilesItemContent>();
    data?.genericComponent.forEach((itemData) => {
      items.push({
        id: itemData.uid || itemData.id,
        title: itemData?.title,
        subTitle: itemData?.subtitle,
        copy: itemData?.copy,
        iconSrc: itemData?.icon[0],
        settings: this.getItemSettings(itemData),
      } as MediaTilesItemContent);
    });
    return {
      hasItemSubTitles: items.some((item) => !!item.subTitle),
      items,
    } as MediaTilesContent;
  }

  public getOrderList(data): OrderListContent {
    const items: OrderListContents[] = [];
    data?.genericComponent.forEach((itemData) => {
      items.push({
        title: itemData.title,
        copy: itemData.copy,
        image: !!itemData?.icon[0]?.url ? environment.strapiURL + itemData.icon[0].url : null,
        iconAlt: itemData.fa_icon_name ? itemData.fa_icon_name : itemData.title,
        settings: this.getItemSettings(itemData),
      });
    });
    return {
      items,
    };
  }

  public getPostcodeCheckerContent(data) {
    return {
      postcodeLabel: data?.genericComponent[0]?.listOfContent[0]?.text,
      postcodeError: data?.genericComponent[0]?.listOfContent[0]?.content,
      lastNameLabel: data?.genericComponent[0]?.listOfContent[1]?.text,
      lastNameError: data?.genericComponent[0]?.listOfContent[1]?.content,
      multiAddressError: data?.genericComponent[0]?.listOfContent[2]?.content,
      houseNumLabel: data?.genericComponent[0]?.listOfContent[3]?.text,
      houseNumError: data?.genericComponent[0]?.listOfContent[3]?.content,
      submitLabel: data?.genericComponent[0]?.ctas[0]?.label,
      submitLink: data?.genericComponent[0]?.ctas[0]?.url,
    } as PostcodeCheckerContent;
  }

  public getUpgrade360Content(data) {
    return {
      firstNameLabel: data?.genericComponent[0]?.listOfContent[0]?.text,
      firstNameError: data?.genericComponent[0]?.listOfContent[0]?.content,
      lastNameLabel: data?.genericComponent[0]?.listOfContent[1]?.text,
      lastNameError: data?.genericComponent[0]?.listOfContent[1]?.content,
      emailLabel: data?.genericComponent[0]?.listOfContent[2]?.text,
      emailError: data?.genericComponent[0]?.listOfContent[2]?.content,
      submitLabel: data?.genericComponent[0]?.ctas[0]?.label,
      submitLink: data?.genericComponent[0]?.ctas[0]?.url,
    } as Upgrade360Content;
  }


  public getSelectableCardList(data): SelectableCardContent {
    const items: SelectableCardContents[] = [];
    data?.genericComponent.forEach((itemData) => {
      items.push({
        images: [{ key: itemData.uid, value: environment.strapiURL + itemData.icon[0].url }],
        tileHeading: itemData.title,
        isRadioOrCheckBox: 'Radio',
        productId: itemData.uid,
        isRadioUncheckAllowed: true,
        settings: this.getItemSettings(itemData),
      });
    });
    return {
      items,
    };
  }

  public getTabContent(data): TabContent {
    const items: TabItemContent[] = [];
    data?.genericComponent.forEach((itemData) => {
      items.push({
        images: [{ key: itemData.uid, value: environment.strapiURL + itemData.icon[0].url }],
        tileHeading: itemData.title,
        isRadioOrCheckBox: 'Radio',
        productId: itemData.uid,
        isRadioUncheckAllowed: true,
        settings: this.getItemSettings(itemData),
      });
    });
    return {
      items,
    };
  }

  public getTextContent(data): TextContent {
    const items = new Array<TextItemContent>();
    data?.genericComponent.forEach((itemData) => {
      items.push({
        id: itemData.uid || itemData.id,
        title: itemData?.title,
        subTitle: itemData?.subtitle,
        copy: itemData?.copy,
        iconSrc: itemData?.icon[0],
        media: this.getMedia(itemData?.media),
        settings: this.getItemSettings(itemData),
      } as TextItemContent);
    });
    return {
      items,
    } as TextContent;
  }

  public getTilesContent(data: any, sesData: any): TilesContent {
    const items = new Array<TileWithFAIconSmallContent>();
    data?.genericComponent.forEach((itemData) => {
      const settings = this.getItemSettings(itemData);
      items.push({
        title: itemData?.title,
        copy: itemData?.copy,
        link: itemData?.ctas[0]?.url,
        icon: itemData?.fa_icon_name,
        iconColor: itemData?.fa_icon_color,
        iconWeight: itemData?.fa_icon_weight ? IconWeight[itemData?.fa_icon_weight?.toUpperCase()] : '',
        iconAlt: itemData?.title,
        iconSrc: itemData?.icon[0],
        iconSize: itemData?.iconSize,
        settings,
        isVisible: this.checkSettings(settings, sesData),
      } as TileWithFAIconSmallContent);
    });
    return {
      items,
    } as TilesContent;
  }

  public getMedia(media: IMedia[]) {
    if (media) {
      const result = new Array<PageSectionItemMedia>();
      media?.forEach((item) => {
        result.push({
          copy: item.copy,
          alt: item.alt,
          url: item.URL,
        } as PageSectionItemMedia);
      });
      return result;
    }
    return null;
  }

  public getCTAs(ctas: ICTA[]) {
    if (ctas) {
      const result = new Array<PageSectionItemCTA>();
      ctas?.forEach((cta) => {
        result.push({
          label: cta.label,
          url: cta.label,
          targetContentSectionId: cta.targetContentSectionID,
        } as PageSectionItemCTA);
      });
      return result;
    }
    return null;
  }

  public getPageSettings(seoLandingPage: ISEOLandingPage): Setting[] {
    const keyValueList = new Array<Setting>();
    seoLandingPage?.elements.forEach((globalComponent) => {
      globalComponent?.genericComponent?.forEach((genericComponent) => {
        genericComponent?.KeyValue?.forEach((keyValue) => {
          const key = keyValue.key.toLowerCase();
          if (key.startsWith(SettingsType.Page)) {
            keyValueList.push({
              key: key?.replace(SettingsType.Page, ''),
              value: keyValue?.value[0]?.value?.toLowerCase(),
            } as Setting);
          }
        });
      });
    });
    return keyValueList;
  }

  public getSectionSettings(
    globalComponent: IGlobalComponent,
    keyValueList: Setting[] = new Array<Setting>()
  ): Setting[] {
    globalComponent?.genericComponent?.forEach((genericComponent) => {
      genericComponent?.KeyValue?.forEach((keyValue) => {
        const key = keyValue.key.toLowerCase();
        if (key.startsWith(SettingsType.Section)) {
          keyValueList.push({
            key: key?.replace(SettingsType.Section, ''),
            value: keyValue?.value[0]?.value?.toLowerCase(),
          } as Setting);
        }
      });
    });
    return keyValueList;
  }

  public getItemSettings(
    genericComponent: IGenericComponent,
    keyValueList: Setting[] = new Array<Setting>()
  ): Setting[] {
    genericComponent?.KeyValue?.forEach((keyValue) => {
      if (!keyValue.key.startsWith(SettingsType.Page) && !keyValue.key.startsWith(SettingsType.Section)) {
        keyValueList.push({
          key: keyValue?.key?.toLowerCase(),
          value: keyValue?.value[0]?.value?.toLowerCase(),
        } as Setting);
      }
    });
    return keyValueList;
  }

  public checkSettings(settings: any[], sesData): boolean {
    let result = true;
    settings.forEach((setting) => {
      switch (setting.key) {
        case Settings.IsIdentified: {
          result =
            (setting?.value === 'true') === (sesData.angularPublicSession.identificationStatus === 'FULLY_IDENTIFIED');
          break;
        }
      }
    });
    return result;
  }
}
